import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import { apiFetch } from '../lib/api';

const useResume = ({ userId, showToast, setUserBio }) => {
  const [resumeFilename, setResumeFilename] = useState('');
  const [uploadingResume, setUploadingResume] = useState(false);
  const [generatingBio, setGeneratingBio] = useState(false);

  useEffect(() => {
    const fetchResumeInfo = async () => {
      if (!userId) {
        setResumeFilename('');
        return;
      }
      const { data, error } = await supabase.from('users').select('resume_filename').eq('id', userId).single();
      if (!error && data) {
        setResumeFilename(data.resume_filename || '');
      }
    };
    fetchResumeInfo();
  }, [userId]);

  const generateBioFromResume = useCallback(
    async ({ skipResumeCheck = false, silentSuccess = false } = {}) => {
      if (!resumeFilename && !skipResumeCheck) {
        showToast('Please upload a resume first.', 'error');
        return;
      }
      if (!userId) return;
      setGeneratingBio(true);
      try {
        const { data: userData, error } = await supabase.from('users').select('resume_text').eq('id', userId).single();
        if (error || !userData?.resume_text) {
          showToast('Resume text not found. Please re-upload your resume.', 'error');
          setGeneratingBio(false);
          return;
        }
        const response = await apiFetch(`/generate_bio_from_resume`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            resume_text: userData.resume_text,
            user_id: userId,
          }),
        });
        const data = await response.json();
        if (response.ok) {
          setUserBio(data.generated_bio);
          if (!silentSuccess) {
            showToast('Biography generated successfully!', 'success');
          }
        } else {
          showToast(data.detail || 'Failed to generate biography.', 'error');
        }
      } catch (error) {
        showToast('Failed to generate biography.', 'error');
      }
      setGeneratingBio(false);
    },
    [resumeFilename, setUserBio, showToast, userId],
  );

  const handleFileUpload = useCallback(
    async file => {
      if (!file || !userId) return;
      if (!file.name.toLowerCase().endsWith('.pdf')) {
        showToast('Please upload a PDF file only.', 'error');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        showToast('File size must be less than 10MB.', 'error');
        return;
      }

      setUploadingResume(true);
      try {
        const formData = new FormData();
        formData.append('file', file);
        const response = await apiFetch(`/upload_resume?user_id=${userId}`, {
          method: 'POST',
          body: formData,
        });
        const data = await response.json();
        if (response.ok) {
          setResumeFilename(file.name);
          showToast('Resume uploaded successfully!', 'success');
          await generateBioFromResume({ skipResumeCheck: true });
        } else {
          showToast(data.detail || 'Failed to upload resume.', 'error');
        }
      } catch (error) {
        showToast('Failed to upload resume.', 'error');
      }
      setUploadingResume(false);
    },
    [generateBioFromResume, showToast, userId],
  );

  const handleFileInputChange = useCallback(
    event => {
      const file = event.target.files?.[0];
      if (file) {
        handleFileUpload(file);
      }
    },
    [handleFileUpload],
  );

  const handleDrop = useCallback(
    event => {
      event.preventDefault();
      const file = event.dataTransfer.files?.[0];
      if (file) {
        handleFileUpload(file);
      }
    },
    [handleFileUpload],
  );

  const handleDragOver = useCallback(event => {
    event.preventDefault();
  }, []);

  const deleteResume = useCallback(async () => {
    if (!userId) return;
    if (!window.confirm('Are you sure you want to delete your uploaded resume?')) {
      return;
    }
    try {
      const response = await apiFetch(`/delete_resume?user_id=${userId}`, {
        method: 'DELETE',
      });
      if (response.ok) {
        setResumeFilename('');
        showToast('Resume deleted successfully.', 'success');
      } else {
        showToast('Failed to delete resume.', 'error');
      }
    } catch (error) {
      showToast('Failed to delete resume.', 'error');
    }
  }, [showToast, userId]);

  return {
    resumeFilename,
    uploadingResume,
    generatingBio,
    handleFileInputChange,
    handleDrop,
    handleDragOver,
    deleteResume,
    generateBioFromResume,
    handleFileUpload,
  };
};

export { useResume };
